import { useEffect, useState } from "react";
import { db } from "./firebase";
import { collection, onSnapshot, query, where, getDocs } from "firebase/firestore";
import { inputStyle, cardStyle, colors } from "./lib/theme";
import { formatDayTime } from "./lib/weekUtils";
import { computeStandings } from "./lib/stats";

export default function StatsPage() {
  var [templates, setTemplates] = useState([]);
  var [users, setUsers] = useState([]);
  var [dependents, setDependents] = useState([]);
  var [filter, setFilter] = useState("all");
  var [matches, setMatches] = useState([]);

  useEffect(function () {
    var unsub = onSnapshot(collection(db, "sessionTemplates"), function (snap) {
      setTemplates(snap.docs.map(function (d) { return { id: d.id, ...d.data() }; }));
    });
    getDocs(collection(db, "users")).then(function (snap) {
      setUsers(snap.docs.map(function (d) { return { uid: d.id, ...d.data() }; }));
    });
    getDocs(collection(db, "dependents")).then(function (snap) {
      setDependents(snap.docs.map(function (d) { var data = d.data(); return { uid: data.linkedUid || d.id, displayName: data.displayName, archived: !!data.archived }; }));
    });
    return unsub;
  }, []);

  useEffect(function () {
    var q = filter === "all"
      ? collection(db, "matches")
      : query(collection(db, "matches"), where("templateId", "==", filter));
    var unsub = onSnapshot(q, function (snap) {
      setMatches(snap.docs.map(function (d) { return { id: d.id, ...d.data() }; }));
    });
    return unsub;
  }, [filter]);

  var names = {};
  users.forEach(function (u) { names[u.uid] = u.displayName; });
  dependents.forEach(function (d) { if (!names[d.uid]) names[d.uid] = d.displayName; });
  var playerIds = Array.from(new Set(users.map(function (u) { return u.uid; }).concat(dependents.filter(function (d) { return !d.archived; }).map(function (d) { return d.uid; }))));
  // League standings only ever reflect league-mode results — social games are
  // recorded (see Recent Results below, and each player's Profile page) but
  // never affect this ranking.
  var leagueMatches = matches.filter(function (m) { return m.mode === "league"; });
  var standings = computeStandings(leagueMatches, playerIds, names);

  var recent = matches.slice().sort(function (a, b) { return new Date(b.timestamp) - new Date(a.timestamp); }).slice(0, 20);

  return (
    <div>
      <h2 style={{ fontSize: 18, marginBottom: 12 }}>Stats</h2>
      <div style={{ marginBottom: 16 }}>
        <select style={inputStyle} value={filter} onChange={function (e) { setFilter(e.target.value); }}>
          <option value="all">All-time (every session)</option>
          {templates.map(function (t) {
            return <option key={t.id} value={t.id}>{formatDayTime(t.dayOfWeek, t.time, t.endTime) + " — " + t.venueName}</option>;
          })}
        </select>
      </div>

      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <h3 style={{ marginTop: 0, fontSize: 14 }}>League Standings</h3>
        <p style={{ fontSize: 12, color: colors.textFaint, marginTop: -6, marginBottom: 12 }}>
          League-mode results only. Social results don't affect this ranking — see your Profile page for those.
        </p>
        {standings.map(function (s, i) {
          return (
            <div key={s.uid} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid " + colors.border, fontSize: 14 }}>
              <span>{(i + 1) + ". " + s.name}</span>
              <span style={{ color: s.games > 0 ? colors.accent : colors.textFaint }}>
                {s.games > 0 ? Math.round(s.winPct * 100) + "% (" + s.wins + "W-" + s.losses + "L" + (s.draws ? "-" + s.draws + "D" : "") + ")" : "no games yet"}
              </span>
            </div>
          );
        })}
      </div>

      <div style={cardStyle}>
        <h3 style={{ marginTop: 0, fontSize: 14 }}>Recent Results</h3>
        {recent.length === 0 && <p style={{ fontSize: 13, color: colors.textDim }}>No results yet.</p>}
        {recent.map(function (m) {
          var teamA = m.teamA || [];
          var teamB = m.teamB || [];
          var teamAName = teamA.map(function (uid) { return names[uid] || uid; }).join(" & ");
          var teamBName = teamB.map(function (uid) { return names[uid] || uid; }).join(" & ");
          var played = m.timestamp ? new Date(m.timestamp).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }) : "";
          return (
            <div key={m.id} style={{ fontSize: 13, padding: "6px 0", borderBottom: "1px solid " + colors.border }}>
              <div>{teamAName} {m.scoreA} — {m.scoreB} {teamBName}</div>
              <div style={{ color: colors.textFaint, marginTop: 2 }}>{played}{played ? " · " : ""}{m.venueName} · {m.mode}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
